import React from 'react';
import { FormattedMessage } from 'react-intl';
import { isSearchEnhancementsMilestone1Enabled } from 'feature-flags';
import { take } from 'lodash';
import { lazyForPaint } from 'react-loosely-lazy';
import { useRouterActions } from 'react-resource-router';
import type { SearchResults } from 'state/ui/search';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { Button } from '@atlassian/help-center-common-component/analytics/button';
import { ThemedButton } from '@atlassian/help-center-common-component/themed-button';
import { withAsync } from '@atlassian/help-center-common-component/with-async';
import { hash } from '@atlassian/help-center-common-util/hash';
import { addParamsToUrl } from '@atlassian/help-center-common-util/history-qs';
import { DEFAULT_MAX_KB_RESULTS_IN_DETAILS_PAGE } from '@atlassian/help-center-common-util/search-providers/search-providers';
import { SearchResultLoading } from '../loading';
import messages from './messages';
import {
    PortalResultEntry,
    KbResultEntry,
    RequestResultEntry,
    ResultTitle,
    SomeResultRoot,
    ExpandArticleResultsButton,
    AllArticleResultsButton,
} from './styled';

export interface ResultClickEvent {
    term: string;
    queryHash: string;
    totalIndex: number;
    totalCount: number;
    category: string;
    categoryCount: number;
    categoryIndex: number;
}

export interface SomeResultProps {
    portalId?: string;
    result: SearchResults;
    term: string;
    queryTime: number;
    className?: string;
    autoFocusOnFirstItem?: boolean;
    itemsFocusable?: boolean;
    isSearchingPortals?: boolean;
    isSearchingRequestTypes?: boolean;
    isSearchingKb?: boolean;
}

const AllArticlesResult = withAsync({
    loader: lazyForPaint(() => import(/* webpackChunkName: "all-articles-result" */ '../../all-articles-result')),
});

const SearchResultSome = ({
    portalId,
    result,
    term,
    queryTime,
    className,
    autoFocusOnFirstItem,
    itemsFocusable,
    isSearchingPortals,
    isSearchingKb,
    isSearchingRequestTypes,
}: SomeResultProps) => {
    const DEFAULT_SEARCH_RESULTS_LIMIT = 3;
    const EXPANDED_SEARCH_RESULTS_LIMIT = 8;
    const [isKbResultsExpanded, setIsKbResultsExpanded] = React.useState(false);
    const KB_RESULTS_COUNT = isKbResultsExpanded ? EXPANDED_SEARCH_RESULTS_LIMIT : DEFAULT_SEARCH_RESULTS_LIMIT;
    const totalResults = result.portals.length + result.requestTypes.length + result.knowledgeBase.articles.length;
    const { push } = useRouterActions();
    let queryHash: string = '';
    let focus = autoFocusOnFirstItem;

    if (!!term) {
        queryHash = hash(`${term}${queryTime}`);
    }

    const shouldFocus = () => {
        if (focus) {
            focus = false;
            return true;
        }
        return false;
    };

    const showExpandArticleResultsButton =
        !isKbResultsExpanded && result.knowledgeBase.totalSize > DEFAULT_SEARCH_RESULTS_LIMIT;

    const showAllArticleResultsButton =
        isKbResultsExpanded && result.knowledgeBase.totalSize > EXPANDED_SEARCH_RESULTS_LIMIT;

    const kbResults = take(result.knowledgeBase.articles, KB_RESULTS_COUNT);

    const handleExpandClick = React.useCallback(() => {
        // Preemptively preload the all articles bundle when users click on show more.
        if (result.knowledgeBase.totalSize > EXPANDED_SEARCH_RESULTS_LIMIT) {
            AllArticlesResult.preload();
        }
        setIsKbResultsExpanded(true);
    }, [result.knowledgeBase.totalSize]);

    const handleMoreArticlesClick = React.useCallback(() => {
        if (portalId) {
            push(addParamsToUrl(`/portal/${portalId}/articles/search`, { searchTerm: term })) as unknown as void;
            return;
        }

        push(addParamsToUrl('/articles/search', { searchTerm: term })) as unknown as void;
    }, [portalId, push, term]);

    const ShowMoreButton = isSearchEnhancementsMilestone1Enabled() ? ThemedButton : Button;

    return (
        <SomeResultRoot className={className}>
            <div data-test-id="articles-result-container">
                {!!result.knowledgeBase.totalSize && (
                    <ResultTitle>
                        <FormattedMessage {...messages.knowledgeBaseResultTitle} />
                    </ResultTitle>
                )}
                {kbResults.map((article, index) => (
                    <AnalyticsContext
                        data={{
                            attributes: {
                                hash: queryHash,
                                q_len: term.length,
                                total_idx: index + 1,
                                total_count: totalResults,
                                category_idx: index + 1,
                                category_count: result.knowledgeBase.articles.length,
                                isSearchingRequestType: isSearchingRequestTypes,
                                isSearchingPortal: isSearchingPortals,
                            },
                        }}
                        key={`article_${article.id}`}
                    >
                        <KbResultEntry
                            portalName={result.knowledgeBase.portalName}
                            portalId={portalId}
                            title={article.name}
                            queryHash={queryHash}
                            // Disabling existing violations, should be fixed when revisited.
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus={shouldFocus()}
                            focusable={itemsFocusable}
                            {...article}
                        />
                    </AnalyticsContext>
                ))}
                <AnalyticsContext
                    data={{
                        attributes: {
                            queryLength: term.length,
                            result_count: result.knowledgeBase.articles.length,
                            total_count: Math.min(
                                result.knowledgeBase.totalSize,
                                DEFAULT_MAX_KB_RESULTS_IN_DETAILS_PAGE
                            ),
                        },
                    }}
                >
                    {showExpandArticleResultsButton && (
                        <ExpandArticleResultsButton>
                            <ShowMoreButton
                                data-test-id="show-more-articles-button"
                                appearance="link"
                                actionSubjectId="showMoreArticlesButton"
                                iconAfter={<ChevronDownIcon label="" />}
                                onClick={handleExpandClick}
                            >
                                <FormattedMessage {...messages.expandArticleResultsButtonText} />
                            </ShowMoreButton>
                        </ExpandArticleResultsButton>
                    )}
                    {showAllArticleResultsButton && (
                        <AllArticleResultsButton>
                            <ShowMoreButton
                                data-test-id="show-more-articles-button"
                                appearance="link"
                                actionSubjectId="viewAllArticlesButton"
                                onClick={handleMoreArticlesClick}
                            >
                                <FormattedMessage
                                    {...messages.allArticleResultsButtonText}
                                    values={{
                                        numArticles: Math.min(
                                            result.knowledgeBase.totalSize,
                                            DEFAULT_MAX_KB_RESULTS_IN_DETAILS_PAGE
                                        ),
                                    }}
                                />
                            </ShowMoreButton>
                        </AllArticleResultsButton>
                    )}
                </AnalyticsContext>
            </div>

            {!isSearchingKb && !isSearchingPortals && (
                <div data-test-id="request-types-result-container">
                    <ProgressiveSearchRender show={isSearchingRequestTypes}>
                        {!!result.requestTypes.length && (
                            <ResultTitle data-test-id="request-type-result-title">
                                <FormattedMessage {...messages.requestTypesResultTitle} />
                            </ResultTitle>
                        )}

                        {result.requestTypes.map((requestType, index) => (
                            <AnalyticsContext
                                data={{
                                    attributes: {
                                        hash: queryHash,
                                        q_len: term.length,
                                        total_idx:
                                            index + result.knowledgeBase.articles.length + result.portals.length + 1,
                                        total_count: totalResults,
                                        category_idx: index + 1,
                                        category_count: result.requestTypes.length,
                                        searchMetaData: requestType.searchMetaData,
                                    },
                                }}
                                key={`request_${requestType.id}`}
                            >
                                <RequestResultEntry
                                    key={`request_${requestType.id}`}
                                    id={requestType.id}
                                    name={requestType.name}
                                    portalId={requestType.portalId}
                                    portalName={requestType.portalName}
                                    iconUrl={requestType.iconUrl}
                                    queryHash={queryHash}
                                    searchTerm={term}
                                    // Disabling existing violations, should be fixed when revisited.
                                    // eslint-disable-next-line jsx-a11y/no-autofocus
                                    autoFocus={shouldFocus()}
                                    focusable={itemsFocusable}
                                />
                            </AnalyticsContext>
                        ))}
                    </ProgressiveSearchRender>
                </div>
            )}

            {!isSearchingKb && (
                <div data-test-id="portals-result-container">
                    <ProgressiveSearchRender show={isSearchingPortals}>
                        {!!result.portals.length && (
                            <ResultTitle>
                                <FormattedMessage {...messages.portalResultTitle} />
                            </ResultTitle>
                        )}

                        {result.portals.map((portal, index) => (
                            <AnalyticsContext
                                data={{
                                    attributes: {
                                        hash: queryHash,
                                        q_len: term.length,
                                        total_idx: index + result.knowledgeBase.articles.length + 1,
                                        total_count: totalResults,
                                        category_idx: index + 1,
                                        category_count: result.portals.length,
                                        isSearchingRequestType: isSearchingRequestTypes,
                                    },
                                }}
                                key={`portal_${portal.id}`}
                            >
                                <PortalResultEntry
                                    id={portal.id}
                                    name={portal.name}
                                    logoUrl={portal.iconUrl}
                                    queryHash={queryHash}
                                    searchTerm={term}
                                    // Disabling existing violations, should be fixed when revisited.
                                    // eslint-disable-next-line jsx-a11y/no-autofocus
                                    autoFocus={shouldFocus()}
                                    focusable={itemsFocusable}
                                />
                            </AnalyticsContext>
                        ))}
                    </ProgressiveSearchRender>
                </div>
            )}
        </SomeResultRoot>
    );
};

export default SearchResultSome;

export const ProgressiveSearchRender = ({ show, children }: { show?: boolean; children: React.ReactNode }) =>
    show ? <SearchResultLoading isRelative /> : <div>{children}</div>;
